/**
 * The tools, and the options belonging to whichever one is in hand.
 *
 * A strip of icons rather than a column of paragraphs: the words are in the
 * tooltips, where they are read once and then never needed again, and the space
 * they were taking goes to the options for the tool actually being used. That
 * is the arrangement every editor of this kind settles on, and for the same
 * reason: what a tool does with a drag is a property of the tool, so it belongs
 * under the tool rather than in a panel about the selection.
 */

import { SELECTS, SELECT_FILTERS, type SelectFilter } from './model';
import { TRANSFORM_TOOLS, type EditorMode, type TransformTool } from './model';
import { Slider } from './Palette';

export function EditorTools({
  mode,
  tool,
  onTool,
  holding,
  filter,
  onFilter,
  onSelectConnected,
  selected,
  brush,
  onBrush,
  round,
  onRound,
  density,
  onDensity,
  vary,
  onVary,
  clipboard,
  onCopy,
  onCut,
  onPaste,
  onDelete,
}: {
  mode: EditorMode;
  tool: TransformTool;
  onTool: (tool: TransformTool) => void;
  /** What is being held in the browser, if anything, so Place can say what it puts down. */
  holding: string | null;
  /** What a drag takes in, which is an option of the Select tool. */
  filter: SelectFilter;
  onFilter: (filter: SelectFilter) => void;
  onSelectConnected: () => void;
  selected: number;
  /** How wide the brush works, and what shape it is. */
  brush: number;
  onBrush: (size: number) => void;
  round: boolean;
  onRound: (round: boolean) => void;
  /** How thickly a wide brush scatters, and whether it varies what it puts down. */
  density: number;
  onDensity: (density: number) => void;
  vary: boolean;
  onVary: (vary: boolean) => void;
  /** How many things are waiting to be put down again. */
  clipboard: number;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDelete: () => void;
}): JSX.Element {
  // Nothing in the ground itself can be rubbed out, moved or turned, so Terrain
  // shows only the tools a landscape has any use for.
  const shown = TRANSFORM_TOOLS.filter((entry) => {
    if (entry.under) return false;
    if (mode !== 'terrain') return true;
    return entry.tool === 'select' || entry.tool === 'brush';
  });

  const says = (entry: (typeof TRANSFORM_TOOLS)[number]): string =>
    entry.tool === 'place' && holding ? `Puts down ${holding}` : entry.hint;

  /** Whether a tool, or one of the ways of using it, is the one in hand. */
  const lit = (entry: (typeof TRANSFORM_TOOLS)[number]): boolean =>
    tool === entry.tool ||
    TRANSFORM_TOOLS.some((sub) => sub.under === entry.tool && sub.tool === tool);

  return (
    <div className="tool-panel">
      {/* The tools themselves, named: a row of glyphs is a row of guesses, and
          these are pressed by people who know what they want. */}
      <div className="tool-strip">
        {shown.map((entry) => (
          <button
            key={entry.tool}
            type="button"
            className={lit(entry) ? 'editor-tool on' : 'editor-tool'}
            title={`${entry.label} (${entry.key}) — ${says(entry)}`}
            onClick={() => onTool(entry.tool)}
          >
            {entry.label} <span className="editor-tool-key">({entry.key})</span>
          </button>
        ))}

        {/* What is done to whatever the tools have hold of. Beside the tools
            rather than up on the bar with the modes: these act on a selection,
            and a selection is what a tool made. */}
        {SELECTS.includes(mode) ? (
          <>
            <span className="tool-separator" />

            <button
              type="button"
              className="editor-tool"
              disabled={selected === 0}
              title="Copy the selection (Ctrl C)"
              onClick={onCopy}
            >
              Copy
            </button>
            <button
              type="button"
              className="editor-tool"
              disabled={selected === 0}
              title="Cut the selection (Ctrl X)"
              onClick={onCut}
            >
              Cut
            </button>
            <button
              type="button"
              className="editor-tool"
              disabled={clipboard === 0}
              title="Put it down where the pointer is (Ctrl V)"
              onClick={onPaste}
            >
              Paste{clipboard > 0 ? ` ${clipboard}` : ''}
            </button>
            <button
              type="button"
              className="editor-tool danger"
              disabled={selected === 0}
              title="Take the selection out of the world (Delete)"
              onClick={onDelete}
            >
              Delete
            </button>
          </>
        ) : null}
      </div>

      <ToolOptions
        mode={mode}
        tool={tool}
        onTool={onTool}
        holding={holding}
        filter={filter}
        onFilter={onFilter}
        onSelectConnected={onSelectConnected}
        selected={selected}
        brush={brush}
        onBrush={onBrush}
        round={round}
        onRound={onRound}
        density={density}
        onDensity={onDensity}
        vary={vary}
        onVary={onVary}
      />
    </div>
  );
}

/** What the tool in hand can be adjusted about, and nothing else. */
function ToolOptions({
  mode,
  tool,
  onTool,
  holding,
  filter,
  onFilter,
  onSelectConnected,
  selected,
  brush,
  onBrush,
  round,
  onRound,
  density,
  onDensity,
  vary,
  onVary,
}: {
  mode: EditorMode;
  tool: TransformTool;
  onTool: (tool: TransformTool) => void;
  holding: string | null;
  filter: SelectFilter;
  onFilter: (filter: SelectFilter) => void;
  onSelectConnected: () => void;
  selected: number;
  brush: number;
  onBrush: (size: number) => void;
  round: boolean;
  onRound: (round: boolean) => void;
  density: number;
  onDensity: (density: number) => void;
  vary: boolean;
  onVary: (vary: boolean) => void;
}): JSX.Element | null {
  const selecting = tool === 'select' || tool === 'lasso';

  if (selecting) {
    return (
      <div className="tool-options">
        <div className="tool-row">
          <span className="tool-label">Drag</span>
          <button
            type="button"
            className={tool === 'select' ? 'editor-tab on' : 'editor-tab'}
            title="Drag a box over what to take in"
            onClick={() => onTool('select')}
          >
            Marquee
          </button>
          <button
            type="button"
            className={tool === 'lasso' ? 'editor-tab on' : 'editor-tab'}
            title="Draw round what to take in (L)"
            onClick={() => onTool('lasso')}
          >
            Lasso
          </button>
        </div>

        <div className="tool-row">
          <span className="tool-label">Filter</span>
          {SELECT_FILTERS.map((entry) => (
            <button
              key={entry.filter}
              type="button"
              className={filter === entry.filter ? 'editor-tab on' : 'editor-tab'}
              onClick={() => onFilter(entry.filter)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="wide"
          disabled={selected === 0}
          title="Follows walls, floors and fences, so a building comes up as one thing"
          onClick={onSelectConnected}
        >
          Select Connected
        </button>

        <p className="hint">Ctrl adds or removes one. Escape puts it all down.</p>
      </div>
    );
  }

  if (tool === 'brush' || tool === 'erase' || (tool === 'place' && mode !== 'build')) {
    return (
      <div className="tool-options">
        <Slider
          label="Width"
          value={brush}
          min={1}
          max={12}
          suffix={brush === 1 ? ' tile' : ' tiles'}
          onChange={onBrush}
        />

        <div className="tool-row">
          <span className="tool-label">Shape</span>
          <button
            type="button"
            className={round ? 'editor-tab' : 'editor-tab on'}
            onClick={() => onRound(false)}
          >
            Square
          </button>
          <button
            type="button"
            className={round ? 'editor-tab on' : 'editor-tab'}
            onClick={() => onRound(true)}
          >
            Round
          </button>
        </div>

        {tool === 'place' ? (
          <>
            {/* Held as a fraction, because that is what the server is sent and
                what it clamps to. Shown as a percentage, because that is how
                anybody thinks about how much of a wood is trees. */}
            <Slider
              label="Strength"
              value={Math.round(density * 100)}
              min={5}
              max={100}
              suffix="%"
              onChange={(percent) => onDensity(percent / 100)}
            />

            <div className="tool-row">
              <span className="tool-label">Sizes</span>
              <button
                type="button"
                className={vary ? 'editor-tab on' : 'editor-tab'}
                title="Puts them down at slightly different sizes"
                onClick={() => onVary(!vary)}
              >
                Vary them
              </button>
            </div>

            <p className="hint">{holding ? `Scattering ${holding}.` : 'Nothing held yet.'}</p>
          </>
        ) : null}
      </div>
    );
  }

  if (tool === 'move' || tool === 'rotate' || tool === 'scale') {
    return (
      <div className="tool-options">
        <p className="hint">
          {selected === 0
            ? 'Nothing held. Select something first.'
            : tool === 'move'
              ? 'Drag the world to move what is held.'
              : tool === 'rotate'
                ? 'Drag left or right to turn it.'
                : 'Drag up or down to resize it.'}
        </p>
      </div>
    );
  }

  return null;
}
